import React from 'react';
import { LegalLayout } from './LegalLayout';

export default function TermsPage() {
  return (
    <LegalLayout title="Terms & Conditions" lastUpdated="9 March 2026">
      <h2>1. Introduction</h2>
      <p>
        These Terms and Conditions ("Terms") govern your access to and use of the BidSentry platform
        ("Platform", "Service"), operated by Bidsentry Ltd ("Company", "we", "us", "our"),
        a company registered in England and Wales (Company No: 17096278),
        with its registered office at 124 City Road, London, EC1V 2NX, United Kingdom.
      </p>
      <p>
        By creating an account, accessing the Platform, or using any of its features, you ("User", "you", "your")
        agree to be bound by these Terms. If you do not agree, you must not use the Platform.
      </p>

      <h2>2. Service Description</h2>
      <p>
        BidSentry provides a subcontractor margin intelligence platform for the UK construction and tendering sector.
        The Service includes bid analysis, commercial modelling, AI-assisted commentary, and related features
        designed to support estimators and commercial managers in evaluating tender opportunities.
      </p>

      <h2>3. Eligibility</h2>
      <p>
        The Platform is intended for use by businesses and professionals in the UK construction sector.
        You must be at least 18 years of age and have the legal capacity to enter into a binding agreement.
        By registering, you represent that the information you provide is accurate and complete.
      </p>

      <h2>4. Account Registration and Security</h2>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials.
        You must notify us immediately of any unauthorised use of your account.
        We reserve the right to suspend or terminate accounts that violate these Terms
        or pose a security risk to the Platform.
      </p>

      <h2>5. Beta Access</h2>
      <p>
        Where access is provided under a beta programme, the Service is provided on an "as-is" and "as-available"
        basis. Beta features may be modified, suspended, or discontinued at any time without notice.
        Data processed during the beta period may be subject to different retention and deletion schedules,
        as communicated separately.
      </p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Platform for any unlawful purpose or in violation of applicable laws and regulations.</li>
        <li>Reverse engineer, decompile, or disassemble any part of the Platform.</li>
        <li>Attempt to gain unauthorised access to other users' accounts or our systems.</li>
        <li>Introduce malicious code, viruses, or any technology harmful to the Platform.</li>
        <li>Scrape, crawl, or use automated tools to extract data from the Platform without our written consent.</li>
        <li>Share your account access with third parties or use another user's account.</li>
      </ul>

      <h2>7. Intellectual Property</h2>
      <p>
        All intellectual property rights in the Platform, including but not limited to software, algorithms,
        designs, text, graphics, and the BidSentry brand, are owned by Bidsentry Ltd or its licensors.
        Nothing in these Terms grants you any right to use our trademarks or proprietary content
        except as expressly permitted.
      </p>
      <p>
        You retain ownership of any data you upload to the Platform ("User Data").
        By uploading User Data, you grant us a limited, non-exclusive licence to process, store,
        and analyse it solely for the purpose of delivering the Service to you.
      </p>

      <h2>8. AI-Generated Content and Disclaimers</h2>
      <p>
        The Platform utilises deterministic commercial modelling and AI-assisted commentary to generate
        analysis, scoring, and recommendations. These outputs are provided for informational and assistive
        purposes only.
      </p>
      <p>
        <strong>The outputs of BidSentry do not constitute professional, financial, or legal advice.</strong>
        You remain solely responsible for all commercial decisions made in reliance on the Platform's outputs.
        We make no warranty that outputs are error-free, complete, or suitable for any particular purpose.
      </p>

      <h2>9. Fees and Payment</h2>
      <p>
        Access to the Platform may be subject to subscription fees as set out on our pricing page.
        All fees are quoted in British Pounds Sterling (GBP) and are exclusive of VAT unless stated otherwise.
        Payment terms, billing cycles, and cancellation policies are as communicated at the point of purchase.
      </p>

      <h2>10. Data Protection</h2>
      <p>
        We process personal data in accordance with the UK General Data Protection Regulation (UK GDPR)
        and the Data Protection Act 2018. Full details of our data processing practices are set out
        in our <a href="/privacy" className="text-orange-600 hover:text-orange-700">Privacy Policy</a>.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Bidsentry Ltd shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages, including but not limited to
        loss of profits, revenue, business, data, or anticipated savings, arising out of or in connection
        with your use of the Platform.
      </p>
      <p>
        Our total aggregate liability in respect of any and all claims arising under or in connection
        with these Terms shall not exceed the total fees paid by you to us in the twelve (12) months
        preceding the claim.
      </p>
      <p>
        Nothing in these Terms excludes or limits our liability for death or personal injury caused by
        negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded
        or limited by English law.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Bidsentry Ltd, its officers, directors, employees,
        and agents from any claims, losses, liabilities, damages, costs, or expenses (including reasonable
        legal fees) arising out of your use of the Platform, your User Data, or your breach of these Terms.
      </p>

      <h2>13. Termination</h2>
      <p>
        Either party may terminate this agreement at any time. You may terminate by closing your account.
        We may suspend or terminate your access if you breach these Terms, upon reasonable notice where practicable.
        Upon termination, your right to access the Platform ceases immediately. We will retain and delete
        your data in accordance with our Privacy Policy and applicable data protection law.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be communicated via email
        or a prominent notice within the Platform. Continued use of the Service after changes take
        effect constitutes acceptance of the revised Terms.
      </p>

      <h2>15. Governing Law and Jurisdiction</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws of England and Wales.
        Any disputes arising under or in connection with these Terms shall be subject to the exclusive
        jurisdiction of the courts of England and Wales.
      </p>

      <h2>16. Severability</h2>
      <p>
        If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
        shall continue in full force and effect.
      </p>

      <h2>17. Entire Agreement</h2>
      <p>
        These Terms, together with the Privacy Policy and Cookie Policy, constitute the entire agreement
        between you and Bidsentry Ltd in relation to your use of the Platform.
      </p>

      <h2>18. Contact</h2>
      <p>
        For questions about these Terms, contact us at:<br />
        <strong>Email:</strong> legal@bidsentry.io<br />
        <strong>Company:</strong> Bidsentry Ltd<br />
        <strong>Registered Office:</strong> 124 City Road, London, EC1V 2NX, United Kingdom
      </p>

      <h2>Company Information</h2>
      <p>
        Bidsentry Ltd is a company registered in England and Wales under company number 17096278.
      </p>
      <p>
        Registered office:<br />
        124 City Road<br />
        London<br />
        EC1V 2NX<br />
        United Kingdom
      </p>
    </LegalLayout>
  );
}
